
import { motion } from "framer-motion";
// Image removed - use img tag;
import { Link } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight, Sparkles, Truck, Shield } from "lucide-react";
import { useCartStore } from "@/lib/store";
import toast from "react-hot-toast";
import { emptyStates } from "@/lib/placeholders";

export default function CartPage() {
  const { items, updateQuantity, removeItem, clearCart, getTotal, getItemCount } = useCartStore();

  const handleQuantityChange = (id: string, newQuantity: number) => {
    if (newQuantity <= 0) {
      removeItem(id);
      toast.success("Item removed from cart");
    } else {
      updateQuantity(id, newQuantity);
    }
  };

  const handleRemoveItem = (id: string) => {
    removeItem(id);
    toast.success("Item removed from cart");
  };

  const subtotal = getTotal();
  const shipping = subtotal > 500 ? 0 : 100;
  const tax = subtotal * 0.18; // 18% GST
  const total = subtotal + shipping + tax;
  const remainingForFreeShipping = Math.max(0, 500 - subtotal);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <main className="flex-grow pt-20 pb-16">
        <div className="container mx-auto px-4 py-8 max-w-6xl">
          {/* Header Section */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <div className="flex items-center justify-between mb-1">
              <h1 className="text-2xl font-semibold font-playfair text-gray-900">
                Shopping Cart
              </h1>
              {items.length > 0 && (
                <span className="text-xs font-medium text-gray-500">
                  {getItemCount()} {getItemCount() === 1 ? "item" : "items"}
                </span>
              )}
            </div>
            {items.length > 0 && (
              <p className="text-sm text-gray-500">Review your items</p>
            )}
          </motion.div>

          {items.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-16"
            >
              <ShoppingBag className="w-20 h-20 text-gray-300 mx-auto mb-4" />
              <h2 className="text-xl font-medium text-gray-800 mb-2">{emptyStates.cart.title}</h2>
              <p className="text-sm text-gray-500 mb-6">{emptyStates.cart.description}</p>
              <Link
                to="/products"
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-all text-sm font-medium"
              >
                <span>Start Shopping</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Cart Items */}
              <div className="lg:col-span-2 space-y-3">
                {items.map((item, index) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="group bg-white rounded-lg border border-gray-200 hover:border-gray-300 transition-colors overflow-hidden"
                  >
                    <div className="p-4 flex gap-4">
                      <Link to={`/products/${item.productId}`} className="flex-shrink-0">
                        <div className="relative w-20 h-20 rounded-md overflow-hidden bg-gray-100">
                          <img
                            src={item.image}
                            alt={item.name}
                            
                            className="object-cover group-hover:scale-105 transition-transform duration-200"
                          />
                        </div>
                      </Link>
                      <div className="flex-grow flex flex-col justify-between min-w-0">
                        <div>
                          <Link to={`/products/${item.productId}`}>
                            <h3 className="font-medium text-sm text-gray-900 mb-1 hover:text-gray-700 transition-colors line-clamp-2">
                              {item.name}
                            </h3>
                          </Link>
                          {item.sku && (
                            <p className="text-xs text-gray-400 mb-2 font-mono">SKU: {item.sku}</p>
                          )}
                          <div className="text-base font-semibold text-gray-900 mb-3">
                            ₹{(item.price * item.quantity).toLocaleString()}
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-0.5 bg-gray-50 rounded-md border border-gray-200">
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                              className="p-1.5 hover:bg-white rounded-md transition-colors text-gray-600 hover:text-gray-900"
                            >
                              <Minus className="w-3.5 h-3.5" />
                            </motion.button>
                            <span className="px-3 py-1.5 text-sm font-medium text-gray-900 min-w-[2.5rem] text-center">
                              {item.quantity}
                            </span>
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                              className="p-1.5 hover:bg-white rounded-md transition-colors text-gray-600 hover:text-gray-900"
                            >
                              <Plus className="w-3.5 h-3.5" />
                            </motion.button>
                          </div>
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleRemoveItem(item.id)}
                            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-all"
                            aria-label="Remove item"
                          >
                            <Trash2 className="w-4 h-4" />
                          </motion.button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Order Summary */}
              <div className="lg:col-span-1">
                <motion.div
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-white rounded-lg border border-gray-200 p-5 sticky top-20"
                >
                  <h2 className="text-base font-semibold text-gray-900 mb-4">Order Summary</h2>

                  <div className="space-y-3 mb-5">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600">Subtotal</span>
                      <span className="font-medium text-gray-900">₹{subtotal.toLocaleString()}</span>
                    </div>
                    <div className="text-xs text-gray-400 text-center -mt-2">
                      ({getItemCount()} {getItemCount() === 1 ? "item" : "items"})
                    </div>
                    
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600 flex items-center gap-1.5">
                        <Truck className="w-3.5 h-3.5" />
                        Shipping
                      </span>
                      <span className="font-medium">
                        {shipping === 0 ? (
                          <span className="text-green-600 text-xs">Free</span>
                        ) : (
                          `₹${shipping.toLocaleString()}`
                        )}
                      </span>
                    </div>
                    
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600">Tax (GST)</span>
                      <span className="font-medium text-gray-900">₹{tax.toFixed(2)}</span>
                    </div>
                    
                    {subtotal < 500 && (
                      <div className="bg-gray-50 rounded-md p-3 border border-gray-200">
                        <p className="text-xs text-gray-600 mb-2">
                          Add ₹{remainingForFreeShipping.toFixed(2)} more for free shipping
                        </p>
                        <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${(subtotal / 500) * 100}%` }}
                            className="h-full bg-gray-900 rounded-full"
                          />
                        </div>
                      </div>
                    )}
                    
                    <div className="border-t border-gray-200 pt-3 mt-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-semibold text-gray-900">Total</span>
                        <span className="text-lg font-semibold text-gray-900">
                          ₹{total.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Link
                      to="/checkout"
                      className="block w-full text-center px-4 py-2.5 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-all text-sm font-medium"
                    >
                      Proceed to Checkout
                    </Link>
                    <Link
                      to="/products"
                      className="block w-full text-center px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all text-sm font-medium"
                    >
                      Continue Shopping
                    </Link>
                  </div>

                  {/* Trust Badges */}
                  <div className="mt-5 pt-4 border-t border-gray-200">
                    <div className="flex items-center justify-center gap-4 text-xs text-gray-400">
                      <div className="flex items-center gap-1">
                        <Shield className="w-3 h-3" />
                        <span>Secure</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Truck className="w-3 h-3" />
                        <span>Fast Delivery</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
